import React from 'react';
import { Github, Linkedin, Mail, ArrowRight, Download, CheckCircle, Trophy } from 'lucide-react';
import { CyclingTypeWriter } from '../../components';
import { scrollToSection, downloadCV } from '../../utils';
import './HeroSection.css';

const DEVICON = 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons';

const techIcons = [
  {
    name: 'Python',
    logo: `${DEVICON}/python/python-original.svg`,
    bg: 'rgba(55,118,171,0.15)',
    border: 'rgba(55,118,171,0.5)',
    delay: '0s',
    posClass: 'tech-pos-python',
    desktopOnly: false,
  },
  {
    name: 'Django',
    logo: `${DEVICON}/django/django-plain.svg`,
    bg: 'rgba(9,120,60,0.25)',
    border: 'rgba(9,180,100,0.5)',
    delay: '0.3s',
    posClass: 'tech-pos-django',
    desktopOnly: false,
  },
  {
    name: 'Git',
    logo: `${DEVICON}/git/git-original.svg`,
    bg: 'rgba(240,80,50,0.15)',
    border: 'rgba(240,80,50,0.5)',
    delay: '0.6s',
    posClass: 'tech-pos-git',
    desktopOnly: true,
  },
  {
    name: 'PostgreSQL',
    logo: `${DEVICON}/postgresql/postgresql-original.svg`,
    bg: 'rgba(51,103,145,0.15)',
    border: 'rgba(51,103,145,0.5)',
    delay: '0.9s',
    posClass: 'tech-pos-postgresql',
    desktopOnly: false,
  },
  {
    name: 'Docker',
    logo: `${DEVICON}/docker/docker-original.svg`,
    bg: 'rgba(36,150,237,0.15)',
    border: 'rgba(36,150,237,0.5)',
    delay: '1.2s',
    posClass: 'tech-pos-docker',
    desktopOnly: false,
  },
  {
    name: 'PHP',
    logo: `${DEVICON}/php/php-original.svg`,
    bg: 'rgba(119,123,180,0.15)',
    border: 'rgba(119,123,180,0.5)',
    delay: '1.5s',
    posClass: 'tech-pos-php',
    desktopOnly: true,
  },
];

const mobileIcons = techIcons.filter((t) => !t.desktopOnly);

const statBadges = [
  { icon: CheckCircle, label: '100% On-Time Delivery', color: '#10b981' },
  { icon: Trophy,      label: '3+ Years Experience',   color: '#f59e0b' },
];

const HeroSection = ({ setActiveSection }) => {
  return (
    <section id="home" className="hero-section">
      <div className="hero-bg-effects">
        <div className="hero-bg-glow hero-bg-glow-1" />
        <div className="hero-bg-glow hero-bg-glow-2" />
        <div className="hero-bg-glow hero-bg-glow-3" />
      </div>

      <div className="hero-container">
        <div className="hero-left animate-slide-left">
          <div className="hero-badge">
            <span className="hero-badge-dot" />
            Available for Freelance
          </div>

          <h1 className="hero-name">
            Ebraheem<br />
            <span className="gradient-text">Wannous</span>
          </h1>

          <div className="hero-role">
            <span className="mono hero-role-bracket">&lt;</span>
            <CyclingTypeWriter
              texts={['Backend Developer', 'AI Enthusiast']}
              speed={120}
            />
            <span className="mono hero-role-bracket">/&gt;</span>
          </div>

          <p className="hero-bio">
            I build robust, scalable backend systems and RESTful APIs using Django and Laravel.
            Passionate about clean architecture, AI-powered solutions, and turning complex problems into elegant code.
          </p>

          <div className="hero-stat-badges">
            {statBadges.map((badge, i) => {
              const Icon = badge.icon;
              return (
                <div key={i} className="hero-stat-badge" style={{ '--badge-color': badge.color }}>
                  <Icon size={14} className="hero-stat-badge-icon" />
                  <span>{badge.label}</span>
                </div>
              );
            })}
          </div>

          <div className="hero-buttons">
            <button
              onClick={() => scrollToSection('projects', setActiveSection)}
              className="hero-btn-primary"
            >
              See My Work
              <ArrowRight size={18} className="btn-arrow" />
            </button>
            <button onClick={downloadCV} className="hero-btn-secondary">
              <Download size={18} />
              Download Resume
            </button>
          </div>

          <div className="hero-social">
            <a href="https://github.com/ebraheem54" target="_blank" rel="noopener noreferrer"
               className="hero-social-link glass glass-hover">
              <Github size={20} />
            </a>
            <a href="https://www.linkedin.com/in/ebraheem-wannous" target="_blank" rel="noopener noreferrer"
               className="hero-social-link glass glass-hover">
              <Linkedin size={20} />
            </a>
            <a href="mailto:ebraheem.sy370@gmail.com"
               className="hero-social-link glass glass-hover">
              <Mail size={20} />
            </a>
          </div>
        </div>

        <div className="hero-right animate-slide-right">
          <div className="hero-visual">
            <div className="hero-photo-ring">
              <div className="hero-photo-wrapper animate-pulse-glow">
                <img
                  src="/ebraheem-photo.jpg"
                  alt="Ebraheem Wannous"
                  className="hero-photo"
                />
              </div>
            </div>

            {techIcons.map((item, i) => (
              <div
                key={i}
                className={`tech-float-icon ${item.posClass}${item.desktopOnly ? ' tech-desktop-only' : ''}`}
                title={item.name}
                style={{
                  backgroundColor: item.bg,
                  border: `1px solid ${item.border}`,
                  animationDelay: item.delay,
                }}
              >
                <img src={item.logo} alt={item.name} width={26} height={26} />
              </div>
            ))}
          </div>

          <div className="tech-mobile-row">
            {mobileIcons.map((item, i) => (
              <div
                key={i}
                className="tech-mobile-icon"
                title={item.name}
                style={{
                  backgroundColor: item.bg,
                  border: `1px solid ${item.border}`,
                }}
              >
                <img src={item.logo} alt={item.name} width={22} height={22} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
